import { Component, OnInit, Input, EventEmitter, Output, Inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material';
import { CurrencyConverterPurePipe } from '../currency-converter-pure.pipe';
import { InsuranceQuoteModalComponent } from 'app/shared/modals/insurance-quote/insurance-quote-modal.component';
@Component({
  selector: 'usb-compare-modal',
  templateUrl: './compare-modal.component.html',
  styleUrls: ['./compare-modal.component.scss'],
  providers: [CurrencyConverterPurePipe]
})
export class CompareModalComponent implements OnInit {
  comparisonData: any[] = [];
  selectedPlans: any[] = [];
  allCoverageNames: string[] = [];

  // Nuevas propiedades para manejar la selección por marca
  groupedPlans: any = {}; // Agrupar planes por marca
  visiblePlansByBrand: any = {}; // Planes visibles por marca
  selectedPlanIndexByBrand: any = {}; // Índice seleccionado por marca
  showSelectorsByBrand: any = {}; // Controlar selectores por marca
  availableBrands: string[] = []; // Marcas disponibles
  isSingleBrand: boolean = false; // Si hay solo una marca

  // Nuevo: mantener el valor actual del select por marca
  currentSelectValueByBrand: any = {};

  @Output() modalCompareClose = new EventEmitter<any>();

  clientSetting: any;
  buttonColorActive: string = '';
  buttonTextActive: string = '';
  currencyAgency: any;
  currencies: any;
  styleSidebar: any;
  hoveredButtons: Set<string> = new Set();

  constructor(
    public dialogRef: MatDialogRef<CompareModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
    public currencyConverterPure: CurrencyConverterPurePipe,
    private dialog: MatDialog
  ) {
    this.initializePlans();
  }

  initializePlans(): void {
    // Agrupar planes por marca
    this.groupedPlans = {};
    this.visiblePlansByBrand = {};
    this.selectedPlanIndexByBrand = {};
    this.showSelectorsByBrand = {};
    this.currentSelectValueByBrand = {};

    this.data.selectedInsurances.forEach((insuranceGroup: any[]) => {
      insuranceGroup.forEach((insurance: any) => {
        const brand = insurance.plan.brand;

        if (!this.groupedPlans[brand]) {
          this.groupedPlans[brand] = [];
        }

        this.groupedPlans[brand].push(insurance);
      });
    });

    // Inicializar marcas disponibles
    this.availableBrands = Object.keys(this.groupedPlans);
    this.isSingleBrand = this.availableBrands.length === 1;

    // Inicializar visibilidad y selectores por marca
    this.availableBrands.forEach(brand => {
      const brandPlans = this.groupedPlans[brand];

      if (this.isSingleBrand) {
        // Si es una sola marca: mostrar hasta 4 planes, selector si hay más de 4
        if (brandPlans.length <= 4) {
          // Mostrar todos los planes (hasta 4)
          this.visiblePlansByBrand[brand] = brandPlans;
          this.showSelectorsByBrand[brand] = false;
        } else {
          // Más de 4 planes, mostrar 4 y usar selector
          this.visiblePlansByBrand[brand] = brandPlans.slice(0, 4);
          this.showSelectorsByBrand[brand] = true;
          this.currentSelectValueByBrand[brand] = 0;
        }
      } else {
        // Múltiples marcas: mostrar 1 plan por marca con selector
        // Inicializar con el primer plan seleccionado
        this.selectedPlanIndexByBrand[brand] = 0;
        this.currentSelectValueByBrand[brand] = 0;
        this.visiblePlansByBrand[brand] = brandPlans.length > 0 ? [brandPlans[0]] : [];
        this.showSelectorsByBrand[brand] = brandPlans.length > 1;
      }
    });

    // Preparar los planes seleccionados para comparación (todos los visibles)
    this.updateSelectedPlans();
  }

  // Cambiar plan visible para una marca específica usando select
  onPlanSelectChange(brand: string, event: any): void {
    const selectedIndex = parseInt(event, 10);
    const allBrandPlans = this.groupedPlans[brand];

    // Actualizar el valor actual del select
    this.currentSelectValueByBrand[brand] = selectedIndex;

    if (this.isSingleBrand) {
      // Para una sola marca: reemplazar todos los planes visibles
      if (selectedIndex === 0) {
        // Mostrar primeros 4 planes
        this.visiblePlansByBrand[brand] = allBrandPlans.slice(0, 4);
      } else {
        // Calcular nuevos planes visibles basado en el índice seleccionado
        const startIndex = selectedIndex * 4;
        this.visiblePlansByBrand[brand] = allBrandPlans.slice(startIndex, startIndex + 4);
      }
      this.selectedPlanIndexByBrand[brand] = selectedIndex;
    } else {
      // Para múltiples marcas: cambiar solo el plan visible de esa marca
      // Verificar que el índice sea válido
      if (selectedIndex >= 0 && selectedIndex < allBrandPlans.length) {
        this.selectedPlanIndexByBrand[brand] = selectedIndex;
        this.visiblePlansByBrand[brand] = [allBrandPlans[selectedIndex]];
      }
    }

    this.updateSelectedPlans();
    this.prepareComparisonData();
  }

  // Obtener opciones para el select de una marca
  getPlanOptions(brand: string): any[] {
    const allBrandPlans = this.groupedPlans[brand];

    if (this.isSingleBrand) {
      // Para una sola marca: agrupar en páginas de 4 planes
      const pages = [];
      for (let i = 0; i < allBrandPlans.length; i += 4) {
        const endIndex = Math.min(i + 4, allBrandPlans.length);
        pages.push({
          index: pages.length,
          label: `Planes ${i + 1}-${endIndex}`,
          plans: allBrandPlans.slice(i, endIndex)
        });
      }
      return pages;
    } else {
      // Para múltiples marcas: lista simple de TODOS los planes
      return allBrandPlans.map((plan: any, index: number) => ({
        index: index,
        label: plan.plan.planName,
        plan: plan,
        isSelected: index === this.selectedPlanIndexByBrand[brand]
      }));
    }
  }

  // Obtener el valor seleccionado actual para un select
  getSelectedValue(brand: string): number {
    return this.currentSelectValueByBrand[brand] !== undefined ? this.currentSelectValueByBrand[brand] : (this.selectedPlanIndexByBrand[brand] || 0);
  }

  // Obtener texto especial para la opción actualmente seleccionada
  getOptionLabel(option: any, brand: string): string {
    if (option.index === this.selectedPlanIndexByBrand[brand]) {
      return `${option.label}`;
    }
    return option.label;
  }

  // Forzar actualización del select (nuevo método)
  refreshSelect(brand: string): void {
    // Forzar cambio de detección actualizando la referencia
    this.currentSelectValueByBrand = {...this.currentSelectValueByBrand};
  }

  // Actualizar todos los planes seleccionados basado en lo visible por marca
  updateSelectedPlans(): void {
    this.selectedPlans = [];
    this.availableBrands.forEach(brand => {
      const plans = this.visiblePlansByBrand[brand] || [];
      const selectedPlansForBrand = plans.map(insurance => insurance.plan);
      this.selectedPlans.push(...selectedPlansForBrand);
    });
  }

  ngOnInit() {
    if (this.selectedPlans && this.selectedPlans.length > 0) {
      this.prepareComparisonData();
      this.currencyAgency = this.data.currencyAgency;
      this.currencies = this.data.currencies;
      this.clientSetting = this.data.clientSetting;
      const bookingFlow_sidebar = JSON.parse(this.clientSetting?.clients_setting_app_booking?.bookingFlow_sidebar);
      this.buttonColorActive = bookingFlow_sidebar?.background_color_header;
      this.buttonTextActive = bookingFlow_sidebar?.color;
    }
  }

  prepareComparisonData(): void {
    const allCoverages = new Set<string>();

    this.selectedPlans.forEach(plan => {
      if (plan && plan.coverage && plan.coverage.coverageList) {
        plan.coverage.coverageList.forEach((coverage: any) => {
          if (coverage && coverage.name) {
            allCoverages.add(coverage.name);
          }
        });
      }
    });

    this.allCoverageNames = Array.from(allCoverages);

    this.comparisonData = this.allCoverageNames.map(coverageName => {
      const row: any = {
        coverageName: coverageName,
        coverageType: '',
        plans: {}
      };

      this.selectedPlans.forEach(plan => {
        if (plan && plan.coverage && plan.coverage.coverageList) {
          const coverage = plan.coverage.coverageList.find((c: any) =>
            c && c.name === coverageName
          );

          row.plans[plan.planId] = {
            exists: !!coverage,
            coverageAmount: coverage?.coverage || ''
          };

          if (coverage && !row.coverageType) {
            row.coverageType = coverage.type;
          }
        }
      });

      return row;
    });

  }

  getPlanById(planId: string): any {
    return this.selectedPlans.find(plan => plan.planId === planId);
  }

  close(): void {
    this.dialogRef.close();
  }

  getData(data): void
  {
    // Método de utilidad: por ahora sin implementación
  }

isValidCoverageAmount(coverageAmount: any): boolean {
  if (coverageAmount === null || coverageAmount === undefined) {
    return false;
  }

  // Convertir a número si es string
  const num = Number(coverageAmount);

  // Verificar si es un número válido y mayor a 0
  return !isNaN(num) && num > 0;
}

/**
 * Capitaliza la primera letra de cada palabra y pasa el resto a minúsculas.
 * Útil para textos que vienen en MAYÚSCULAS del backend
 * (ej. "MEDICAMENTOS" -> "Medicamentos").
 */
capitalize(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value
    .toString()
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

setButtonHover(planId: string, isHovered: boolean): void {
  if (isHovered) {
    this.hoveredButtons.add(planId);
  } else {
    this.hoveredButtons.delete(planId);
  }
}

isButtonHovered(planId: string): boolean {
  return this.hoveredButtons.has(planId);
}

buttonRedirectPlan(insurance): void {
  this.dialogRef.close();

  const paxesSelected: any[] = this.data?.paxesSelected || [];
  const insurancePlan: any[] = paxesSelected.length > 0
    ? paxesSelected.map((pax: any) => ({ ...pax, insurance: insurance }))
    : [{ insurance: insurance, age: 30, addons: [] }];

  this.dialog.open(InsuranceQuoteModalComponent, {
    width: 'auto',
    maxWidth: '1440px',
    panelClass: 'insurance-quote-modal-wide',
    data: {
      clientSetting: this.clientSetting,
      currencies: this.currencies,
      currencyAgency: this.currencyAgency,
      insurancePlan: insurancePlan,
      quote: true
    },
    disableClose: false
  });
}

getPrincipalCurrencyFontSize(insurance): string {
  const price = insurance.plan?.pricing?.promotions && insurance.plan?.pricing?.promotions.length > 0
    ? insurance.plan.pricing?.promotions[0].finalPrice
    : insurance.plan.totalPriceProf;
  const convertedPrice = this.currencyConverterPure.transform(price, insurance.plan.pricing.currency, this.currencyAgency?.code, this.currencies) ?? price;
  const digitsOnly = String(convertedPrice).replace(/[^0-9]/g, '');
  return digitsOnly.length <= 7 ? '30px' : '24px';
}

/**
 * Lista plana de todos los planes visibles (en orden de marca),
 * para iterar las cards en una sola grilla sin importar si hay
 * una o varias marcas.
 */
get flatVisiblePlans(): any[] {
  const flat: any[] = [];
  this.availableBrands.forEach(brand => {
    const plans = this.visiblePlansByBrand[brand] || [];
    plans.forEach(insurance => flat.push(insurance));
  });
  return flat;
}

/**
 * CSS Grid template:
 *   - Primera columna fija (300px) para los nombres de cobertura
 *   - Siempre 4 columnas 1fr para los planes (reales o placeholders)
 * Así, si hay menos de 4 planes seleccionados, las columnas restantes
 * se llenan con cards placeholder para mantener la grilla estable.
 */
get gridTemplateColumns(): string {
  return '200px repeat(4, 1fr)';
}

/**
 * Máximo de cards (reales) que se muestran en la grilla.
 * Si hay más planes visibles, se truncan a 4.
 */
get visiblePlanLimit(): number {
  return 4;
}

  /**
   * Lista de planes visibles limitada a `visiblePlanLimit` (4).
   * Es la que se usa para renderizar las cards y las celdas de cobertura.
   */
  get flatVisiblePlansLimited(): any[] {
    return this.flatVisiblePlans.slice(0, this.visiblePlanLimit);
  }

  /**
   * Cantidad de cards placeholder (fantasma) que faltan para completar
   * las 4 columnas de la grilla.
   *
   * Devuelve un array de NÚMEROS (primitivos) cacheado, no objetos.
   * Si devolviéramos objetos nuevos en cada llamada, Angular perdería
   * el trackeo por identidad de referencia y rerenderizaría las cards
   * placeholder en cada ciclo de change detection.
   */
  private _placeholderSlotsCache: number[] = [];
  get placeholderSlots(): number[] {
    const targetLength = Math.max(0, this.visiblePlanLimit - this.flatVisiblePlansLimited.length);
    if (this._placeholderSlotsCache.length !== targetLength) {
      this._placeholderSlotsCache = Array.from({ length: targetLength }, (_, i) => i);
    }
    return this._placeholderSlotsCache;
  }

  /**
   * trackBy para los ngFor que iteran placeholders.
   * Devuelve el índice (estable) en vez de la referencia del item.
   */
  trackByIndex(index: number): number {
    return index;
  }

/**
 * Devuelve true si AL MENOS uno de los planes visibles
 * tiene un selector de plan (es decir, su marca tiene más
 * de 1 plan). Se usa para fijar la altura del header de
 * TODAS las cards y que mantengan la misma altura aunque
 * algunas no tengan select.
 */
get hasAnyPlanSelector(): boolean {
  return this.flatVisiblePlans.some(
    (insurance) => this.showSelectorsByBrand[insurance.plan.brand]
  );
}

}
